export const FIRM = {
  name: "Mizan Audit & Assurance LLP",
  shortName: "Mizan",
  tagline: "Audit. Assurance. Integrity.",
  address: "Orphane Tower, 8th Floor, Room 804, Taleh Road",
  city: "Mogadishu, Somalia",
  phone: "+252 61 9501950",
  phoneHref: "tel:+252619501950",
  email: "info@mizanaudit.com",
  emailHref: "mailto:info@mizanaudit.com",
  hours: "Sat–Thu · 08:00 – 17:00 EAT",
  logo: "https://customer-assets.emergentagent.com/job_a982e078-cc65-49a9-b999-52aa320e43f1/artifacts/5iewvd8k_ChatGPT%20Image%20May%2016%2C%202026%2C%2011_31_54%20AM.png",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Insights", to: "/insights" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "external-audit",
    title: "External Audit",
    short: "Independent statutory audits of financial statements.",
    description:
      "Independent audit opinions on statutory financial statements in compliance with International Standards on Auditing (ISA). We deliver substance, not signatures—our audit work surfaces meaningful insight for boards, regulators and lenders.",
    deliverables: [
      "Audited financial statements with audit opinion",
      "Management letter with control observations",
      "Board / Audit committee report",
    ],
  },
  {
    slug: "internal-audit",
    title: "Internal Audit",
    short: "Risk‑based internal audit, co‑sourced or fully outsourced.",
    description:
      "Risk‑based internal audit programs that strengthen governance and operational discipline. Available as fully outsourced, co‑sourced or as a one‑off review for a specific cycle.",
    deliverables: [
      "Annual risk‑based audit plan",
      "Process & control reviews",
      "Quarterly audit committee reporting",
    ],
  },
  {
    slug: "tax-advisory",
    title: "Tax Advisory",
    short: "Somali tax compliance, planning and dispute resolution.",
    description:
      "End‑to‑end tax compliance and advisory for Somali and cross‑border operations. We help businesses stay compliant with the Federal Revenue Authority and plan tax‑efficient structures.",
    deliverables: [
      "Monthly & annual tax filings",
      "Tax health checks and planning",
      "Representation in tax audits",
    ],
  },
  {
    slug: "ifrs-advisory",
    title: "IFRS Advisory",
    short: "IFRS adoption, conversion and complex accounting opinions.",
    description:
      "From first‑time IFRS adoption to opinions on complex transactions—revenue (IFRS 15), leases (IFRS 16), expected credit losses (IFRS 9) and business combinations (IFRS 3). Built for finance leaders who need clarity.",
    deliverables: [
      "IFRS gap assessment & roadmap",
      "Technical accounting memos",
      "Disclosure templates & training",
    ],
  },
  {
    slug: "risk-compliance",
    title: "Risk & Compliance",
    short: "Enterprise risk, AML/CFT and regulatory compliance reviews.",
    description:
      "Enterprise risk management, anti‑money‑laundering (AML/CFT) frameworks and regulatory compliance reviews tailored to banking, MFIs, NGOs and family businesses operating in Somalia.",
    deliverables: [
      "Risk register & control matrix",
      "AML/CFT program design & review",
      "Regulatory readiness assessments",
    ],
  },
  {
    slug: "forensic-audit",
    title: "Forensic Audit",
    short: "Fraud investigations, dispute support and asset tracing.",
    description:
      "Forensic accounting and fraud investigation engagements—from suspected procurement fraud to shareholder disputes. Independent, evidence‑led work that holds up under scrutiny.",
    deliverables: [
      "Forensic investigation report",
      "Expert‑witness support",
      "Remediation & control redesign",
    ],
  },
  {
    slug: "bookkeeping",
    title: "Bookkeeping & Outsourced Finance",
    short: "Cloud bookkeeping, payroll and management accounts.",
    description:
      "Cloud‑based bookkeeping, payroll and management accounts for SMEs, NGOs and startups. We become your back‑office so leadership can focus on growth.",
    deliverables: [
      "Monthly management accounts",
      "Payroll & statutory filings",
      "Cash‑flow & budget tracking",
    ],
  },
];

export const INDUSTRIES = [
  { name: "Banking & Financial Services", note: "Banks, MFIs, MTBs, fintech." },
  { name: "Telecommunications", note: "MNOs, ISPs and digital infrastructure." },
  { name: "NGOs & Development", note: "INGOs, donor‑funded programs, foundations." },
  { name: "Trade & Logistics", note: "Importers, distributors, freight & shipping." },
  { name: "Construction & Real Estate", note: "Contractors, developers, REITs." },
  { name: "Hospitality & Retail", note: "Hotels, F&B groups, retail chains." },
  { name: "Energy & Utilities", note: "Power, fuel distribution, renewables." },
  { name: "Public Sector & SOEs", note: "Ministries, agencies, state enterprises." },
];

export const STATS = [
  { value: "120+", label: "Engagements delivered" },
  { value: "8", label: "Industries served" },
  { value: "15+", label: "Years combined experience" },
  { value: "ISA", label: "Compliant methodology" },
];

export const VALUES = [
  {
    title: "Independence",
    body: "We answer to evidence, not expectation. Our opinions are the product of disciplined fieldwork and ethical clarity.",
  },
  {
    title: "Precision",
    body: "Numbers tell a story only when checked, reconciled and understood. We work to a standard that holds in any boardroom.",
  },
  {
    title: "Trust",
    body: "Mizan—\u201Cthe balance\u201D—is the standard we hold ourselves to. Confidentiality, integrity and fair dealing are non‑negotiable.",
  },
  {
    title: "Local insight, global standards",
    body: "Deep understanding of the Somali market, executed to International Standards on Auditing and IFRS.",
  },
];

export const INSIGHTS = [
  {
    slug: "ifrs-9-for-somali-banks",
    title: "IFRS 9 for Somali Banks: Practical ECL Modelling",
    date: "March 12, 2026",
    readTime: "8 min read",
    excerpt:
      "Why off‑the‑shelf ECL models fail in frontier markets, and a pragmatic framework banks can adopt without overspending.",
    category: "Banking",
  },
  {
    slug: "audit-committee-charter",
    title: "What a Modern Audit Committee Charter Should Cover",
    date: "February 27, 2026",
    readTime: "6 min read",
    excerpt:
      "A reference template for audit committees of Somali corporates and NGOs—scope, cadence and reporting lines.",
    category: "Governance",
  },
  {
    slug: "tax-residency-somalia",
    title: "Tax Residency & Permanent Establishment in Somalia",
    date: "January 18, 2026",
    readTime: "10 min read",
    excerpt:
      "How cross‑border firms should think about PE risk under current Somali tax practice.",
    category: "Tax",
  },
  {
    slug: "ngo-donor-compliance",
    title: "Donor Compliance: Six Audit Findings That Recur",
    date: "December 04, 2025",
    readTime: "7 min read",
    excerpt:
      "Recurring findings in INGO project audits and how to design controls that survive donor scrutiny.",
    category: "NGO & Development",
  },
];

export const OPEN_ROLES = [
  {
    title: "Senior Audit Manager",
    location: "Mogadishu, Somalia",
    type: "Full‑time",
    description:
      "Lead audit engagements end‑to‑end, supervise teams of 3–6, and own client relationships across banking and corporate portfolios.",
  },
  {
    title: "Audit Associate (IFRS)",
    location: "Mogadishu, Somalia",
    type: "Full‑time",
    description:
      "Execute audit fieldwork under ISA, draft working papers and management letters. ACCA / CPA students strongly encouraged.",
  },
  {
    title: "Tax Consultant",
    location: "Mogadishu, Somalia",
    type: "Full‑time",
    description:
      "Advise clients on Somali direct & indirect tax matters, prepare filings and represent clients in tax audits.",
  },
  {
    title: "Internal Audit Specialist",
    location: "Mogadishu / Hybrid",
    type: "Full‑time",
    description:
      "Deliver risk‑based internal audit reviews for outsourced engagements across multiple industries.",
  },
];
